import { useEffect, useState } from "react";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useRequestResetMutation, useSignInMutation } from "@/src/api/Auth";
import useAuth from "@/src/hooks/useAuth";
import AppForm from "@/src/components/Form";
import { LoginFormValuesType, RequestResetFormValuesType } from "@/src/components/Form/schemas";
import { loginTenantRoute } from "@/src/routes/router";
import { useToast } from "@/components/ui/use-toast";
import { Button } from "@/components/ui/button";
import useLocalStorage from "@/src/hooks/useLocalStorage";

function LoginTenantPage() {
  const { onSignIn } = useAuth();
  const [viewMode, setViewMode] = useState<"login" | "resetPass">("login");
  const isLoginMode = viewMode === "login";
  const search = useSearch({
    strict: false,
  }) as { redirect?: string };
  const { tenant } = loginTenantRoute.useParams();
  const [_mainTenant, setMainTenant] = useLocalStorage("mainTenant", "");
  useEffect(() => {
    setMainTenant(tenant);
  }, []);
  const navigate = useNavigate();
  const { toast } = useToast();

  const { mutate: signIn } = useSignInMutation({
    onSuccess: (data) => {
      onSignIn({
        accessToken: data.accessToken,
        refreshToken: data.refreshToken,
        isLoggedIn: true,
      });
      setTimeout(() => {
        navigate({ to: search?.redirect || "/$tenant/procedures", params: { tenant } });
      });
    },
    onError: () => toast({ title: "Błąd", description: "Wystąpił błąd", variant: "destructive" }),
  });
  const { mutate: requestReset } = useRequestResetMutation({
    onSuccess: () =>
      toast({
        title: "Udało się",
        description:
          "Twoja prośba o zmianę hasła została wysłana. Jeśli posiadasz konto z podanym adrese e-mail, niebawem powinieneś otrzymać link umożliwiający zmianę hasła",
        variant: "success",
      }),
    onError: () => toast({ title: "Błąd", description: "Wystąpił błąd", variant: "destructive" }),
  });

  function onLoginSubmit(values: LoginFormValuesType) {
    signIn({ ...values, tenant });
  }
  function onRequestResetSubmit(values: RequestResetFormValuesType) {
    requestReset({ ...values, tenant });
  }

  function handleForgetPasswordClick() {
    setViewMode("resetPass");
  }
  function handleBackToLoginClick() {
    setViewMode("login");
  }
  return (
    <div className="min-h-screen w-full flex items-center justify-center p-6 bg-gray-50">
      <div className="w-full max-w-md bg-white border border-gray-200 rounded-xl px-8 py-10 flex flex-col shadow-sm">
        {isLoginMode ? (
          <>
            <h1 className="text-2xl font-semibold">Logowanie</h1>
            <hr className="my-5" />
            <AppForm.Login onSubmit={onLoginSubmit} />
            <div className="flex flex-wrap items-center justify-between gap-3 mt-4">
              <Button onClick={handleForgetPasswordClick} className="underline p-0 h-auto" variant="ghost">
                Nie pamiętasz hasła?
              </Button>
              <p className="text-sm">
                Nie masz konta?{" "}
                <Link className="font-medium underline underline-offset-2" to="/$tenant/register" params={{ tenant }}>
                  Zarejestruj się tutaj
                </Link>
              </p>
            </div>
          </>
        ) : (
          <>
            <h1 className="text-2xl font-semibold">Reset hasła</h1>
            <hr className="my-5" />
            <AppForm.RequestReset onSubmit={onRequestResetSubmit} />
            <div className="flex flex-wrap items-center justify-between gap-3 mt-4">
              <Button onClick={handleBackToLoginClick} className="underline p-0 h-auto" variant="ghost">
                Wróć do logowania
              </Button>
              <p className="text-sm">
                Nie masz konta?{" "}
                <Link className="font-medium underline underline-offset-2" to="/$tenant/register" params={{ tenant }}>
                  Zarejestruj się tutaj
                </Link>
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default LoginTenantPage;
